/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useCallback, useMemo } from "react";
import PerfectScrollbar from "react-perfect-scrollbar";
import BootstrapTable from "react-bootstrap-table-next";
import ToolkitProvider from "react-bootstrap-table2-toolkit";
import { debounce } from "lodash";

// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import {
  Badge,
  Box,
  CircularProgress,
  Grid,
  Tooltip,
  Typography,
  IconButton,
} from "@material-ui/core";

// @material-ui/icons components
import {
  Create,
  Refresh as RefreshIcon,
  LocalShipping as LocalShippingIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AddCircle as AddCircleIcon,
} from "@material-ui/icons";

// Karsten
import IconButtonLoader from "components/Buttons/IconButtonLoader";
import ToolkitTable from "components/Cards/Tables/ToolkitTable";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import OccurencesDialog from "components/Dialogs/OccurencesDialog";
import MaskAndValid from "utils/maskAndValid";
import Api from "lib/api/httpRequestHelper";
import { downloadXML, downloadPDF, chaveNFFormatter } from "utils/tables-utils";
import { columnTextField, columnNumberField } from "utils/tables-utils";
import ImagePlaceholder from "components/Image/ImagePlaceholder";
import { utils, writeFileXLSX } from "xlsx";
import useLogs from "hooks/logs";

const useCustomStyles = makeStyles((theme) => ({
  badge: ({ status }) => ({
    backgroundColor:
      status === "ok"
        ? theme.palette.success.main
        : theme.palette.warning.light,
    height: 20,
    width: 20,
    borderRadius: 10,
    border: "none",
  }),
  root: {
    margin: "0 0.5rem",
  },
  buttonLabel: {
    display: "flex",
    alignItems: "center",
  },
  errorModal: {
    padding: theme.spacing(2, 4, 3),
    paddingBottom: 0,
    textAlign: "center",
  },
}));

const ColoredStatus = ({ status, textoOcorrencia }) => {
  const classes = useCustomStyles({ status });

  return (
    <Tooltip
      title={
        status === "ok"
          ? "Entrada de NF Ok"
          : status === "pending"
          ? "Entrada de NF Pendente"
          : textoOcorrencia
      }
      placement="top"
    >
      <Badge
        overlap="circle"
        badgeContent=" "
        color="primary"
        classes={{ badge: classes.badge, root: classes.root }}
      />
    </Tooltip>
  );
};

const getOccurrenceStatus = (success, entradaNf, notaManual) => {
  if (success) {
    if (
      !MaskAndValid.isNullOrEmpty(entradaNf) ||
      !MaskAndValid.isNullOrEmpty(notaManual)
    ) {
      return "ok";
    } else {
      return "pending";
    }
  } else {
    return "error";
  }
};

const getColumnModel = (
  fetchOccurencesData,
  toggleFormDialog,
  setSelectedRow
) => [
  {
    dataField: "SucessoUltimaOcorrencia",
    text: "",
    align: "center",
    headerAlign: "center",
    formatter: (sucessoUltimaOcorrencia, row) => {
      const status = getOccurrenceStatus(
        row.UltimaOcorrenciaEntrega,
        row.DataEntradaNf,
        row?.NotaManual?.NumeroNF
      );

      return (
        <ColoredStatus
          status={status}
          textoOcorrencia={row.UltimaOcorrenciaEntrega}
        />
      );
    },
  },
  columnTextField({
    dataField: "LojaDestino",
    text: "Loja Destino",
  }),
  columnTextField({
    dataField: "DataEmissaoNf",
    text: "Data Emissao NF",
  }),
  columnNumberField({
    dataField: "NumeroNf",
    text: "Numero NF",
  }),
  columnTextField({
    dataField: "SerieNf",
    text: "Serie NF",
    headerAlign: "center",
    align: "center",
  }),
  columnNumberField({
    dataField: "QtdVolumesNf",
    text: "Qtd. de Volumes NF",
    align: "right",
  }),
  columnTextField({
    dataField: "ValorLiquidoNf",
    text: "Valor Liquido NF",
    csvText: "Valor Liquido NF",
    formatter: (cell) => `R$${cell}`,
  }),
  columnTextField({
    dataField: "NomeTransportadora",
    text: "Nome Transportadora",
  }),
  columnTextField({
    dataField: "DataEntradaNf",
    text: "Data Entrada NF",
  }),
  columnTextField({
    dataField: "UltimaOcorrenciaEntrega",
    text: "Ultima Ocorrencia Entrega",
  }),
  columnTextField({
    dataField: "DataUltimaOcorrencia",
    text: "Data Ultima Ocorrencia",
  }),
  columnTextField({
    dataField: "DataUltimaSincronizacaoOcorrencia",
    text: "Data Ultima Sincronizacao Ocorrencia",
  }),
  {
    dataField: "SucessoUltimaOcorrencia",
    headerAlign: "center",
    align: "center",
    text: "Registro de Entrada",
    sort: false,
    formatter: (success, row) => {
      const status = getOccurrenceStatus(
        success,
        row.DataEntradaNf,
        row?.NotaManual?.NumeroNF
      );

      const handleReturnItem = () => {
        if (row?.NotaManual?.NumeroNF) {
          return (
            <Tooltip
              title={!row.DataEntradaNf || "Editar registro de entrada de nota"}
            >
              <span>
                <IconButton
                  aria-label="edit"
                  style={{ margin: "-0.5rem 0" }}
                  onClick={() => {
                    toggleFormDialog("edit");
                    setSelectedRow(row);
                  }}
                >
                  <Box
                    component={Create}
                    width="1.25rem!important"
                    height="1.25rem!important"
                  />
                </IconButton>
              </span>
            </Tooltip>
          );
        }

        if (status !== "ok") {
          return (
            <Tooltip
              title={
                !row.DataEntradaNf || "Inserir registro de entrada de nota"
              }
            >
              <span>
                <IconButton
                  aria-label="create"
                  style={{ margin: "-0.5rem 0" }}
                  onClick={() => {
                    toggleFormDialog("create");
                    setSelectedRow(row);
                  }}
                >
                  <Box
                    component={AddCircleIcon}
                    width="1.25rem!important"
                    height="1.25rem!important"
                  />
                </IconButton>
              </span>
            </Tooltip>
          );
        }
      };

      return <>{handleReturnItem()}</>;
    },
    csvExport: false,
  },
  {
    dataField: "ChaveNf",
    headerAlign: "center",
    align: "center",
    text: "Chave NF",
    sort: false,
    formatter: chaveNFFormatter,
  },
  {
    dataField: "verOcorrencias",
    isDummyField: true,
    headerAlign: "center",
    align: "center",
    text: "Rastreio",
    sort: false,
    formatter: (cell, row) => (
      <div
        style={{
          display: "flex",
          align: "center",
          justifyContent: "center",
        }}
      >
        {!MaskAndValid.isNullOrEmpty(row.UltimaOcorrenciaEntrega) &&
        !MaskAndValid.isNullOrEmpty(row.ChaveNf) ? (
          <IconButtonLoader
            title="Ver Ocorrências de Entrega da Nota"
            size="small"
            onClick={() => fetchOccurencesData(row)}
            icon={<LocalShippingIcon />}
          />
        ) : null}
      </div>
    ),
    csvExport: false,
  },
  {
    dataField: "XmlNoS3",
    text: "XML",
    headerAlign: "center",
    align: "center",
    isDummyField: true,
    formatter: downloadXML,
    csvExport: false,
  },
  {
    dataField: "PdfNoS3",
    text: "PDF",
    headerAlign: "center",
    align: "center",
    isDummyField: true,
    formatter: downloadPDF,
    csvExport: false,
  },
];

const modelTableItemDetail = [
  {
    dataField: "UrlFoto",
    text: "Imagem",
    align: "center",
    headerAlign: "center",
    isDummyField: true,
    formatter: (cell, row) => (
      <div style={{ display: "flex", justifyContent: "center" }}>
        <ImagePlaceholder
          src={row.UrlFoto}
          alt="Foto do produto"
          width={50}
          widthOnError={40}
          imagePreviewTitle={`Produto ${row?.CodigoProduto}`}
        />
      </div>
    ),
    csvExport: true,
  },
  columnNumberField(
    {
      dataField: "CodigoProduto",
      text: "Código",
      align: "center",
    },
    true
  ),
  columnTextField(
    {
      dataField: "LinhaProduto",
      text: "Linha",
    },
    true
  ),
  columnNumberField(
    {
      dataField: "QtdFaturada",
      text: "Quantidade",
    },
    true
  ),
  columnTextField(
    {
      dataField: "DescricaoProduto",
      text: "Descrição",
    },
    true
  ),
];

const exportIconStyle = {
  width: 25,
  height: 25,
};

const getProductList = (dataTable) => {
  const productList = [];

  dataTable?.forEach((data) => {
    data?.ListaProdutosExport?.forEach((product) => {
      if (product.Codigo !== null) {
        productList.push({
          Loja: product?.LojaDestino,
          NF: product?.NF,
          Codigo: product?.Codigo,
          Quantidade: product?.QtdFaturada,
          Descricao: product?.Descricao,
        });
      }
    });
  });

  return productList;
};

const getReceiptList = (dataTable) => {
  const receiptList = [];

  dataTable?.forEach((data) => {
    if (data?.Codigo === null) {
      const newData = {
        LojaDestino: data?.LojaDestino,
        DataEmissaoNf: data?.DataEmissaoNf,
        NumeroNf: data?.NumeroNf,
        SerieNf: data?.SerieNf,
        QtdVolumesNf: data?.QtdVolumesNf,
        ValorLiquidoNf: data?.ValorLiquidoNf,
        NomeTransportadora: data?.NomeTransportadora,
        DataEntradaNf: data?.DataEntradaNf,
        UltimaOcorrenciaEntrega: data?.UltimaOcorrenciaEntrega,
        DataUltimaOcorrencia: data?.DataUltimaSincronizacaoOcorrencia,
        ChaveNf: data?.ChaveNf,
      };
      receiptList.push(newData);
    }
  });

  return receiptList;
};

export default function TableRecebimentoNFs({
  dataTable,
  searchTitles,
  tableTitle,
  isMobile,
  toggleFormDialog,
  setSelectedRow,
  hasSearched,
  currentPage,
  currentSizePerPage,
  handlePageChange,
  handleSizePerPageChange,
}) {
  const snackbar = useSnackbar();

  const { setLocalStorageApiProblem } = useLogs();

  const [ocorrenciasNF, setOcorrenciasNF] = useState(null);
  const [listDetails, setListDetails] = useState([]);
  const [searchingDetails, setSearchingDetails] = useState(false);
  const [hasNoItems, setHasNoItems] = useState(false);

  const dataProducts = useMemo(
    () => (dataTable ? getProductList(dataTable) : []),
    [dataTable]
  );

  const dataReceipt = useMemo(
    () => (dataTable ? getReceiptList(dataTable) : []),
    [dataTable]
  );

  function handleDownloadXML() {
    const receipt = utils.json_to_sheet(dataReceipt);
    const products = utils.json_to_sheet(dataProducts);

    const wb = utils.book_new();
    utils.book_append_sheet(wb, receipt, "Recebimentos NFs");
    utils.book_append_sheet(wb, products, "Lista de Produtos");
    writeFileXLSX(wb, "RecebimentoNFs.xlsx");
  }

  const exportCmp = (
    <span>
      <Tooltip title="Exportar tudo">
        <IconButtonLoader
          onClick={handleDownloadXML}
          icon={
            <img
              src="https://img.icons8.com/material-outlined/72/000000/xls-export.png"
              alt="ícone clicável para exportar a tabela no formato Excel"
              style={exportIconStyle}
            />
          }
          size="small"
        />
      </Tooltip>
    </span>
  );

  const fetchOccurencesData = useCallback(
    async (row) => {
      await Api.get(
        "tracking-occurrences",
        "sellinorder",
        "?chaveNF=" + row.ChaveNf,
        true
      )
        .then((value) => {
          if (
            value?.ErrorMessage ===
            "HTTPERRO 422 Não foi possível encontrar ocorrências de entrega. Verifique os parâmetros informados."
          ) {
            snackbar.showMessage(
              "Não foi possível encontrar ocorrências de entrega para esse recebimento.",
              {
                severity: "info",
              }
            );
          } else if (!value || value.Result?.Ocorrencias.length === 0) {
            snackbar.showMessage(
              "Não foram encontradas ocorrências de entrega para esse recebimento.",
              {
                severity: "info",
              }
            );
          } else {
            setOcorrenciasNF({ ...row, ...value.Result });
          }
        })
        .catch((error) => {
          snackbar.showMessage(
            "No momento não há ocorrência de transporte para esse recebimento, tente novamente mais tarde.",
            {
              severity: "error",
            }
          );
          console.log(error);
          setLocalStorageApiProblem({
            idMenuAccess: "110",
            screen: "Relatório Recebimento de NFs",
            problem: `Erro na chamada do endpoint tracking-occurrences - ${error}`,
          });
        });
    },
    [snackbar]
  );

  const indexedData = dataTable?.map((data, index) => ({ Id: index, ...data }));

  const handleSearchDetails = debounce((row) => {
    setSearchingDetails(true);
    return Api.get(
      "list-itens-invoices",
      "invoice",
      "?numeroNF=" + row.NumeroNf,
      true
    )
      .then((res) => {
        setListDetails(res?.Result || []);
        if (res?.Result?.length === 0) {
          setHasNoItems(true);
        }
      })
      .catch((error) => {
        console.log(error);
        setLocalStorageApiProblem({
          idMenuAccess: "110",
          screen: "Relatório Recebimento de NFs",
          problem: `Erro na chamada do endpoint list-itens-invoices - ${error}`,
        });
      })
      .finally(() => {
        setSearchingDetails(false);
      });
  }, 500);

  const expandRow = {
    renderer: () => (
      <>
        {searchingDetails ? (
          <Grid item xs={6}>
            <CircularProgress style={exportIconStyle} />
          </Grid>
        ) : (
          <>
            {listDetails?.length !== 0 ? (
              <Grid item xs={6}>
                <ToolkitProvider
                  data={listDetails}
                  keyField={"IdProduto"}
                  columns={modelTableItemDetail}
                >
                  {(props) => (
                    <PerfectScrollbar>
                      <BootstrapTable
                        {...props.baseProps}
                        bootstrap4={true}
                        bordered={false}
                        condensed={true}
                        striped={true}
                        hover={true}
                        style={{ overflow: "auto!important" }}
                      />
                    </PerfectScrollbar>
                  )}
                </ToolkitProvider>
              </Grid>
            ) : (
              <>
                {listDetails?.length === 0 && hasNoItems && (
                  <Grid
                    item
                    xs={6}
                    style={{
                      display: "flex",
                      justifyContent: isMobile ? "flex-start" : "center",
                      flexDirection: isMobile ? "column" : "row",
                      marginBottom: "-1rem",
                    }}
                  >
                    <p
                      style={{
                        color: "#DF767D",
                        fontWeight: "bold",
                        fontSize: "0.8rem",
                        marginBottom: isMobile && "-0.2rem",
                      }}
                    >
                      Itens da nota indisponíveis para consulta no momento.
                    </p>
                    <p
                      style={{
                        color: "#DF767D",
                        fontWeight: "bold",
                        fontSize: "0.8rem",
                      }}
                    >
                      Tente novamente mais tarde.
                    </p>
                  </Grid>
                )}
              </>
            )}
          </>
        )}
      </>
    ),
    onExpand: (row) => {
      handleSearchDetails(row);
    },
    onlyOneExpanding: true,
    showExpandColumn: true,
    expandHeaderColumnRenderer: () => (
      <div
        style={{ cursor: "none" }}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {" "}
      </div>
    ),
    expandColumnRenderer: ({ expanded }) => {
      setSearchingDetails(true);
      setHasNoItems(false);
      setListDetails([]);
      if (expanded) {
        return <ExpandLessIcon />;
      }
      return <ExpandMoreIcon />;
    },
  };

  return (
    <>
      <ToolkitTable
        searched={hasSearched}
        keyField="Id"
        dataTable={indexedData}
        columnModel={getColumnModel(
          fetchOccurencesData,
          toggleFormDialog,
          setSelectedRow
        )}
        headerTitle={`RecebimentoNFs`}
        csvFileName="RecebimentoNFs.csv"
        showSearch={false}
        pagination={{
          options: [25, 50, 100],
          defaultOption: currentSizePerPage,
          totalSize: dataTable?.length,
          showTotal: true,
          page: currentPage,
          onPageChange: handlePageChange,
          sizePerPage: currentSizePerPage,
          onSizePerPageChange: handleSizePerPageChange,
          alwaysShowAllBtns: true,
        }}
        CustomHeader={() => (
          <Grid
            container
            className="pl-4"
            style={{ padding: "0 24px", marginBottom: "1rem" }}
          >
            <Grid item xs={isMobile ? 12 : 10}>
              <Box
                component={Typography}
                variant="h2"
                marginBottom="0!important"
              >
                <Box component="span">{tableTitle}</Box>
                <IconButtonLoader
                  size="medium"
                  extraStyle={{
                    width: 24,
                    height: 24,
                    marginLeft: "0.3rem",
                  }}
                  title="Recarregar lista"
                  onClick={searchTitles}
                  icon={<RefreshIcon color="primary" />}
                  aria-label="refresh"
                  color="primary"
                />
              </Box>
            </Grid>
            <Grid
              item
              xs={isMobile ? 12 : 2}
              style={{ marginTop: isMobile && "0.5rem" }}
            >
              <Box
                display="flex"
                justifyContent={isMobile ? "flex-start" : "flex-end"}
              >
                {exportCmp}
              </Box>
            </Grid>
          </Grid>
        )}
        bootstrapTableProps={{
          expandRow,
        }}
        showPrint
        notFoundTitle={
          "Sua pesquisa não encontrou nenhum recebimento correspondente."
        }
      />
      {ocorrenciasNF ? (
        <OccurencesDialog
          data={ocorrenciasNF}
          handleClose={() => setOcorrenciasNF(null)}
          dialogTitle={`Ocorrências de Entrega da Nota ${ocorrenciasNF.NumeroNf} - ${ocorrenciasNF.SerieNf}`}
        />
      ) : null}
    </>
  );
}
